<script setup>
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import OverviewAiPanel from './OverviewAiPanel.vue'
import OverviewCanvas from './OverviewCanvas.vue'
import OverviewPublishBar from './OverviewPublishBar.vue'
import OverviewStatusTag from './OverviewStatusTag.vue'
import WordImportPanel from './WordImportPanel.vue'
import { useMarketOverviewDocument } from '@/composables/useMarketOverviewDocument.js'
import { useMarketOverviewDraft } from '@/composables/useMarketOverviewDraft.js'

// RED-317 §8 — one cell of the matrix (or a national card) opened. Without a
// draft it shows the current version on the Studio canvas with the versions
// strip, and offers Muuda / Alusta teise maakonna tekstist / Laadi Wordist /
// Laadi alla Wordina (an empty cell: Alusta tühjalt instead of Muuda). With a
// draft it is the editor: title and period, the canvas (autosaved), Word in
// and out, and Avalda with its "Kehtib alates" date. Non-modal, so the next
// cell can be clicked while it is open. Inline feedback only, no toasts.
const props = defineProps({
  visible: { type: Boolean, required: true },
  cell: { type: Object, default: null },
  overview: { type: Object, default: null },
  countyLabel: { type: String, default: null },
  purposeLabel: { type: String, default: '' },
  section: { type: String, default: 'market' },
  // Other counties with a published text of this purpose: [{ value, label }].
  sourceCounties: { type: Array, default: () => [] },
  // The Maa-amet statistics table the AI draft cites (T13…); null = the AI card is disabled.
  trykis: { type: String, default: null },
})

const emit = defineEmits(['update:visible', 'changed'])

const { t } = useI18n()
const confirm = useConfirm()
const reader = useMarketOverviewDocument()
const editor = useMarketOverviewDraft()
const { versions, document, loading, failed } = reader

/** This cell's overview id; set once an empty cell gets its row. */
const overviewId = ref(null)
/** The version on screen in read mode; null = the current one. */
const shownVersion = shallowRef(null)
const importOpen = ref(false)
const publishErrors = ref({})
const title = ref('')
const period = ref(null)
const downloadMenu = useTemplateRef('downloadMenu')
const canvas = useTemplateRef('canvas')
// The draft's editor, once the canvas has mounted it (the AI card writes its suggestion there).
const draftEditor = computed(() => canvas.value?.editor ?? null)

const isMarket = computed(() => props.section === 'market')
const isRegional = computed(() => isMarket.value && props.cell?.county !== null && props.cell?.county !== undefined)
const hasPublished = computed(() => Boolean(props.overview?.current))
const editing = computed(() => editor.hasDraft.value)
// A draft is being fetched: neither the editor nor the read side yet.
const draftPending = computed(() => editor.loading.value && !editor.hasDraft.value)
const cellKey = computed(() => (props.cell ? `${props.cell.county ?? ''}|${props.cell.purpose}` : null))
// Editing needs the Word page's full width; reading is the prototype's narrow drawer.
const drawerWidth = computed(() => (editing.value || draftPending.value ? 'md:w-[48rem]!' : 'md:w-[34rem]!'))

const openRead = () => {
  if (overviewId.value !== null && hasPublished.value) {
    reader.open(overviewId.value, shownVersion.value)
  } else {
    reader.reset()
  }
}

watch(
  () => [props.visible, cellKey.value],
  ([isVisible]) => {
    // Another cell: never show the previous cell's text while this one loads.
    reader.reset()
    editor.reset()
    shownVersion.value = null
    importOpen.value = false
    publishErrors.value = {}
    overviewId.value = props.overview?.id ?? null
    if (!isVisible || overviewId.value === null) {
      return
    }
    if (props.overview?.has_draft) {
      editor.load(overviewId.value)
    }
    openRead()
  },
  { immediate: true },
)

// A publish (or a generation run) moved the current version: show it.
watch(
  () => props.overview?.current?.number,
  (number, previous) => {
    if (props.visible && number !== previous && !editing.value) {
      shownVersion.value = null
      openRead()
    }
  },
)

// The draft's title and period, whenever a new draft is adopted.
watch(
  () => editor.document.value,
  payload => {
    title.value = payload?.draft?.title ?? ''
    period.value = payload?.draft?.period ?? null
    publishErrors.value = {}
  },
)

const onTitle = value => {
  title.value = value
  editor.setField('title', value)
  publishErrors.value = { ...publishErrors.value, title: undefined }
}

const onPeriod = value => {
  period.value = value
  editor.setField('period', value)
  publishErrors.value = { ...publishErrors.value, period: undefined }
}

/** Quarters (market) or half-years (economy) around today, the current one first. */
const periodOptions = computed(() => {
  const today = new Date()
  const year = today.getFullYear()
  if (!isMarket.value) {
    const half = today.getMonth() < 6 ? 0 : 1
    const labels = ['I', 'II']
    return [
      `${labels[half]} pa ${year}`,
      half === 0 ? `II pa ${year}` : `I pa ${year + 1}`,
      half === 0 ? `II pa ${year - 1}` : `I pa ${year}`,
    ]
  }
  const labels = ['I', 'II', 'III', 'IV']
  const quarter = Math.floor(today.getMonth() / 3)
  return [0, 1, -1, -2].map(offset => {
    const index = quarter + offset
    const shifted = ((index % 4) + 4) % 4
    return `${labels[shifted]} kv ${year + Math.floor(index / 4)}`
  })
})

/** An empty cell gets its overview row before its first action. */
const ensureOverview = async () => {
  if (overviewId.value === null) {
    overviewId.value = await editor.ensureCell(props.cell)
  }
  return overviewId.value
}

const afterChange = ok => {
  if (ok) {
    emit('changed')
  }
}

const startFrom = async (from, county = null) => {
  const run = async () => {
    const id = await ensureOverview()
    if (id !== null) {
      afterChange(await editor.start(id, from, county))
    }
  }
  if (!editing.value) {
    return run()
  }
  confirm.require({
    header: t('messages.admin.market_overviews.editor.replace_confirm_header'),
    message: t('messages.admin.market_overviews.editor.replace_confirm'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('messages.admin.market_overviews.editor.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('messages.admin.market_overviews.editor.replace_accept'), severity: 'danger' },
    accept: run,
  })
}

const countyPick = ref(null)
const onCountyPick = county => {
  countyPick.value = null
  if (county) {
    startFrom('county', county)
  }
}

const openImport = async () => {
  importOpen.value = true
}

const uploadWord = async file => {
  const id = await ensureOverview()
  return id === null ? null : editor.upload(id, file)
}

const applyWord = async (token, range) => {
  const run = () => editor.applyImport(overviewId.value, token, range)
  const done = await run()
  afterChange(done)
  return done
}

const discard = () => {
  confirm.require({
    header: t('messages.admin.market_overviews.editor.discard_confirm_header'),
    message: t('messages.admin.market_overviews.editor.discard_confirm'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('messages.admin.market_overviews.editor.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('messages.admin.market_overviews.editor.discard_accept'), severity: 'danger' },
    accept: async () => {
      afterChange(await editor.discard(overviewId.value))
      openRead()
    },
  })
}

const downloadUrl = params => route('admin.market-overview.download', { marketOverview: overviewId.value, ...params })

const download = async params => {
  if (params.draft && !(await editor.flush())) {
    return
  }
  window.location.href = downloadUrl(params)
}

const downloadItems = computed(() => [
  { label: t('messages.admin.market_overviews.editor.download_draft'), icon: 'pi pi-pencil', command: () => download({ draft: 1 }) },
  ...(hasPublished.value
    ? [{ label: t('messages.admin.market_overviews.editor.download_current', { number: props.overview.current.number }), icon: 'pi pi-lock', command: () => download({}) }]
    : []),
])

const onDownload = event => {
  if (editing.value) {
    downloadMenu.value?.toggle(event)
  } else {
    download(shownVersion.value === null ? {} : { version: shownVersion.value })
  }
}

const published = () => {
  const id = overviewId.value
  editor.reset()
  overviewId.value = id
  shownVersion.value = null
  // The partial reload already brought the new current version in.
  openRead()
  emit('changed')
}

// Sulge and Escape alike: pending edits are saved first, a failed save keeps the drawer open.
const close = async () => {
  if (editing.value && !(await editor.flush())) {
    return
  }
  emit('update:visible', false)
}

const showVersion = number => {
  const current = versions.value.find(version => version.current)?.number ?? null
  shownVersion.value = number === current ? null : number
  openRead()
}

const shownNumber = computed(() => document.value?.version?.number ?? null)

const formatDate = value => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('et-EE') : '')
const formatTime = iso => (iso ? new Date(iso).toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' }) : '')

const sourceText = version => {
  if (version.source.kind === 'mall') {
    return version.source.label
      ? t('messages.admin.market_overviews.drawer.source_mall', { label: version.source.label })
      : t('messages.admin.market_overviews.drawer.source_mall_unknown')
  }
  return version.source.label ?? ''
}

// The version a report dated today gets, as the matrix cell shows it.
const headerTag = computed(() => {
  const live = props.overview?.live
  return {
    status: props.overview?.status ?? 'missing',
    text: live ? `${live.period ?? ''} · ${t('messages.admin.market_overviews.drawer.valid_from', { date: formatDate(live.valid_from) })}` : null,
  }
})

const scheduledText = date => t('messages.admin.market_overviews.status.scheduled', { date: formatDate(date) })

const saveText = computed(() => {
  switch (editor.saveState.value) {
    case 'dirty':
      return t('messages.admin.market_overviews.editor.state.dirty')
    case 'saving':
      return t('messages.admin.market_overviews.editor.state.saving')
    case 'saved':
      return t('messages.admin.market_overviews.editor.state.saved', { time: formatTime(editor.draft.value?.updated_at) })
    case 'error':
      return t('messages.admin.market_overviews.editor.state.error')
    default:
      return editor.draft.value?.updated_at
        ? t('messages.admin.market_overviews.editor.state.last', { name: editor.draft.value.updated_by ?? '', time: formatTime(editor.draft.value.updated_at) })
        : t('messages.admin.market_overviews.editor.state.clean')
  }
})

const fieldError = name => editor.errors.value[name] ?? publishErrors.value[name] ?? null

/** For the page: save pending edits before another cell opens. */
defineExpose({ flush: () => (editing.value ? editor.flush() : Promise.resolve(true)) })
</script>

<template>
  <Drawer
    :visible="visible"
    position="right"
    :modal="false"
    :dismissable="false"
    :show-close-icon="false"
    :pt="{ root: { class: ['w-full!', drawerWidth] }, content: { class: 'flex flex-col gap-4' } }"
    data-testid="mo-drawer"
    @update:visible="value => (value ? emit('update:visible', true) : close())"
  >
    <template #header>
      <div v-if="cell" class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">
          {{ countyLabel ?? $t('messages.admin.market_overviews.drawer.national') }}
        </span>
        <span class="text-lg font-semibold text-gray-900" data-testid="mo-drawer-title">{{ purposeLabel }}</span>
        <div class="flex items-center gap-2">
          <OverviewStatusTag :status="headerTag.status" :text="headerTag.text" />
          <span v-if="overview?.scheduled" class="text-xs font-medium text-sky-700" data-testid="mo-drawer-scheduled">{{ scheduledText(overview.scheduled.valid_from) }}</span>
          <Tag v-if="editing" severity="info" :value="$t('messages.admin.market_overviews.editor.draft_badge')" class="px-1.5! py-0! text-[0.65rem]!" data-testid="mo-draft-badge" />
        </div>
      </div>
    </template>

    <template v-if="cell">
      <div v-if="draftPending" class="flex flex-col gap-3" data-testid="mo-drawer-loading">
        <Skeleton height="1.25rem" width="60%" />
        <Skeleton height="16rem" />
      </div>

      <div v-else-if="editor.loadFailed.value" class="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="mo-draft-error">
        <span class="text-sm text-red-700">{{ $t('messages.admin.market_overviews.editor.load_error') }}</span>
        <Button type="button" size="small" outlined severity="danger" :label="$t('messages.admin.market_overviews.drawer.retry')" @click="editor.load(overviewId)" />
      </div>

      <!-- The editor: a draft exists. -->
      <template v-else-if="editing">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="flex flex-col gap-1 sm:col-span-2">
            <label for="mo-title" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.editor.title_label') }}</label>
            <InputText id="mo-title" :model-value="title" :invalid="Boolean(fieldError('title'))" fluid data-testid="mo-title" @update:model-value="onTitle" />
            <span class="text-xs" :class="fieldError('title') ? 'text-red-600' : 'text-gray-500'" data-testid="mo-title-hint">
              {{ fieldError('title') ?? (isMarket ? $t('messages.admin.market_overviews.editor.title_hint_market') : $t('messages.admin.market_overviews.editor.title_hint_economy')) }}
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <label for="mo-period" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.editor.period_label') }}</label>
            <Select
              input-id="mo-period"
              :model-value="period"
              :options="periodOptions"
              editable
              :placeholder="$t('messages.admin.market_overviews.editor.period_placeholder')"
              :invalid="Boolean(fieldError('period'))"
              fluid
              data-testid="mo-period"
              @update:model-value="onPeriod"
            />
            <span class="text-xs text-red-600" :class="{ invisible: !fieldError('period') }" data-testid="mo-period-error">{{ fieldError('period') ?? '·' }}</span>
          </div>
          <div v-if="isRegional" class="flex flex-col gap-1">
            <label for="mo-from-county" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.editor.start_from_county') }}</label>
            <Select
              input-id="mo-from-county"
              :model-value="countyPick"
              :options="sourceCounties"
              option-label="label"
              option-value="value"
              :placeholder="sourceCounties.length ? $t('messages.admin.market_overviews.editor.start_from_county_placeholder') : $t('messages.admin.market_overviews.editor.start_from_county_empty')"
              :disabled="!sourceCounties.length || editor.busy.value !== null"
              fluid
              data-testid="mo-from-county"
              @update:model-value="onCountyPick"
            />
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <Button type="button" size="small" outlined icon="pi pi-file-import" :label="$t('messages.admin.market_overviews.editor.import')" data-testid="mo-import-open" @click="openImport" />
          <Button type="button" size="small" outlined icon="pi pi-download" :label="$t('messages.admin.market_overviews.editor.download')" data-testid="mo-download" @click="onDownload" />
          <Menu ref="downloadMenu" :model="downloadItems" popup data-testid="mo-download-menu" />
          <Button
            type="button"
            size="small"
            text
            severity="danger"
            icon="pi pi-trash"
            class="ml-auto"
            :label="$t('messages.admin.market_overviews.editor.discard')"
            :loading="editor.busy.value === 'discard'"
            data-testid="mo-discard"
            @click="discard"
          />
        </div>

        <WordImportPanel
          v-if="importOpen"
          :upload="uploadWord"
          :apply="applyWord"
          :errors="editor.errors.value"
          :busy="editor.busy.value"
          :market="isMarket"
          @done="importOpen = false"
          @cancel="importOpen = false"
        />

        <Message v-if="editor.conflict.value" severity="warn" data-testid="mo-conflict">
          <div class="flex flex-col gap-2">
            <span>
              {{
                $t('messages.admin.market_overviews.editor.conflict', {
                  name: editor.conflict.value.updatedBy ?? $t('messages.admin.market_overviews.editor.someone'),
                  time: formatTime(editor.conflict.value.updatedAt),
                })
              }}
            </span>
            <div class="flex gap-2">
              <Button type="button" size="small" outlined :label="$t('messages.admin.market_overviews.editor.conflict_reload')" data-testid="mo-conflict-reload" @click="editor.reloadAfterConflict()" />
              <Button type="button" size="small" severity="warn" :label="$t('messages.admin.market_overviews.editor.conflict_overwrite')" data-testid="mo-conflict-overwrite" @click="editor.overwrite()" />
            </div>
          </div>
        </Message>

        <Message v-if="fieldError('doc') || fieldError('from') || fieldError('county') || editor.errors.value.action || fieldError('draft')" severity="error" size="small" data-testid="mo-editor-error">
          {{ fieldError('doc') ?? fieldError('from') ?? fieldError('county') ?? fieldError('draft') ?? $t('messages.admin.market_overviews.editor.action_error') }}
        </Message>

        <OverviewAiPanel
          v-if="editor.document.value"
          :key="`ai-${overviewId}`"
          :overview-id="overviewId"
          :trykis="trykis"
          :period="period"
          :editor="draftEditor"
        />

        <OverviewCanvas
          v-if="editor.document.value"
          ref="canvas"
          :overview-id="overviewId"
          :document="editor.document.value"
          editable
          :upload-picture="file => editor.uploadPicture(overviewId, file)"
          @update="editor.markDirty"
        />
        <p v-if="editor.document.value?.warnings?.length" class="text-xs text-gray-500">{{ $t('messages.admin.market_overviews.drawer.warnings') }}</p>
      </template>

      <!-- Read side: no draft. -->
      <template v-else>
        <div class="flex flex-wrap items-end gap-2" data-testid="mo-actions">
          <Button
            v-if="hasPublished"
            type="button"
            size="small"
            icon="pi pi-pencil"
            :label="$t('messages.admin.market_overviews.editor.edit')"
            :loading="editor.busy.value === 'start'"
            data-testid="mo-edit"
            @click="startFrom('current')"
          />
          <Button
            v-else
            type="button"
            size="small"
            icon="pi pi-file"
            :label="$t('messages.admin.market_overviews.editor.start_blank')"
            :loading="editor.busy.value === 'start'"
            data-testid="mo-start-blank"
            @click="startFrom('blank')"
          />
          <Button type="button" size="small" outlined icon="pi pi-file-import" :label="$t('messages.admin.market_overviews.editor.import')" data-testid="mo-import-open" @click="openImport" />
          <Button v-if="hasPublished" type="button" size="small" outlined icon="pi pi-download" :label="$t('messages.admin.market_overviews.editor.download')" data-testid="mo-download" @click="onDownload" />
          <div v-if="isRegional" class="flex min-w-56 flex-col gap-1">
            <label for="mo-from-county-read" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.editor.start_from_county') }}</label>
            <Select
              input-id="mo-from-county-read"
              :model-value="countyPick"
              :options="sourceCounties"
              option-label="label"
              option-value="value"
              size="small"
              :placeholder="sourceCounties.length ? $t('messages.admin.market_overviews.editor.start_from_county_placeholder') : $t('messages.admin.market_overviews.editor.start_from_county_empty')"
              :disabled="!sourceCounties.length || editor.busy.value !== null"
              data-testid="mo-from-county"
              @update:model-value="onCountyPick"
            />
          </div>
        </div>
        <p v-if="editor.errors.value.action || fieldError('from') || fieldError('county')" class="text-sm text-red-600" data-testid="mo-action-error">
          {{ fieldError('from') ?? fieldError('county') ?? $t('messages.admin.market_overviews.editor.action_error') }}
        </p>

        <WordImportPanel
          v-if="importOpen"
          :upload="uploadWord"
          :apply="applyWord"
          :errors="editor.errors.value"
          :busy="editor.busy.value"
          :market="isMarket"
          @done="importOpen = false"
          @cancel="importOpen = false"
        />

        <div v-if="!hasPublished" class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5" data-testid="mo-drawer-empty">
          <p class="text-sm font-medium text-gray-800">{{ $t('messages.admin.market_overviews.drawer.empty_title') }}</p>
          <p class="mt-1 text-sm text-gray-600">{{ $t('messages.admin.market_overviews.drawer.empty') }}</p>
        </div>

        <template v-else>
          <div v-if="failed" class="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="mo-drawer-error">
            <span class="text-sm text-red-700">{{ $t('messages.admin.market_overviews.drawer.load_error') }}</span>
            <Button type="button" size="small" outlined severity="danger" :label="$t('messages.admin.market_overviews.drawer.retry')" @click="openRead" />
          </div>

          <div v-else-if="loading && !document" class="flex flex-col gap-3" :aria-label="$t('messages.admin.market_overviews.drawer.loading')">
            <Skeleton height="1.25rem" width="60%" />
            <Skeleton height="16rem" />
          </div>

          <template v-else-if="document">
            <p class="text-base font-medium text-gray-800" data-testid="mo-drawer-version-title">{{ document.version.title }}</p>
            <div :class="{ 'opacity-60': loading }">
              <OverviewCanvas :overview-id="overviewId" :document="document" />
            </div>
          </template>
        </template>
      </template>

      <section v-if="versions.length" class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5" data-testid="mo-versions">
        <h4 class="mb-1.5 text-xs font-semibold text-gray-700">
          <i class="pi pi-lock text-[0.6rem]" /> {{ $t('messages.admin.market_overviews.drawer.versions') }}
        </h4>
        <p v-if="editing" class="mb-1.5 text-xs text-gray-500">{{ $t('messages.admin.market_overviews.publish.hint') }}</p>
        <ul class="flex flex-col">
          <li v-for="version in versions" :key="version.number">
            <button
              type="button"
              class="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded-sm px-2 py-1.5 text-left text-xs text-gray-600"
              :class="[
                editing ? 'cursor-default' : 'cursor-pointer hover:bg-white',
                { 'bg-white ring-1 ring-primary-300': !editing && shownNumber === version.number },
              ]"
              :disabled="editing"
              :aria-pressed="!editing && shownNumber === version.number"
              :data-testid="`mo-version-${version.number}`"
              @click="showVersion(version.number)"
            >
              <span class="font-semibold text-gray-800">{{ $t('messages.admin.market_overviews.drawer.version_label', { number: version.number }) }}</span>
              <span>· {{ version.period }}</span>
              <span>· {{ $t('messages.admin.market_overviews.drawer.valid_from', { date: formatDate(version.valid_from) }) }}</span>
              <span v-if="sourceText(version)">· {{ sourceText(version) }}</span>
              <Tag v-if="version.scheduled" severity="info" :value="scheduledText(version.valid_from)" class="px-1.5! py-0! text-[0.65rem]!" :data-testid="`mo-version-${version.number}-scheduled`" />
              <Tag v-else-if="version.live" severity="success" :value="$t('messages.admin.market_overviews.drawer.current')" class="px-1.5! py-0! text-[0.65rem]!" :data-testid="`mo-version-${version.number}-live`" />
            </button>
          </li>
        </ul>
      </section>
    </template>

    <template #footer>
      <div class="flex flex-wrap items-end justify-between gap-3 border-t border-gray-100 pt-3">
        <span class="pb-2 text-xs text-gray-500" data-testid="mo-save-state">{{ editing ? saveText : '' }}</span>
        <div class="ml-auto flex flex-wrap items-start gap-2">
          <Button type="button" text severity="secondary" class="mt-5" :label="$t('messages.admin.market_overviews.drawer.close')" data-testid="mo-drawer-close" @click="close" />
          <OverviewPublishBar
            v-if="editing && overviewId !== null"
            :key="`publish-${overviewId}`"
            :overview-id="overviewId"
            :draft-sha="editor.draft.value?.sha ?? null"
            :before-publish="editor.flush"
            @errors="errors => (publishErrors = errors)"
            @published="published"
          />
        </div>
      </div>
    </template>
  </Drawer>
</template>
